/**
 * AttachmentEventHelper - Shared functionality for handling attachment-related events in Trix editors
 *
 * This helper provides common attachment event handling logic that can be mixed into
 * Stimulus controllers that use Trix editors with file upload capabilities.
 *
 * Features:
 * - DRY code by centralizing common event logic
 * - Proper memory leak prevention with cleanup functions
 * - Support for user interaction detection
 * - Attachment event handling
 */
export const AttachmentEventHelper = {
  /**
   * Sets up all attachment and user interaction events for a Trix editor
   * @param {HTMLElement} trixEditor - The Trix editor element
   * @param {Function} onUserInteraction - Callback to mark user interaction
   * @param {Function} onContentChange - Optional callback to trigger when content changes (for attachments)
   * @returns {Object} Object containing cleanup function to remove all event listeners
   */
  setupAllEvents(trixEditor, onUserInteraction, onContentChange = null) {
    if (!trixEditor || typeof onUserInteraction !== 'function') {
      console.warn('AttachmentEventHelper: Invalid trixEditor or onUserInteraction callback')
      return { cleanup: () => {} }
    }

    // Create event handlers that we can reference for cleanup
    const keydownHandler = () => { onUserInteraction() }
    const mousedownHandler = () => { onUserInteraction() }

    const attachmentRemoveHandler = () => {
      onUserInteraction()
      // Trigger content change for attachment removal
      if (onContentChange) {
        onContentChange()
      }
    }

    const fileUploadedHandler = (event) => {
      if (event.detail.editorId === trixEditor.id) {
        onUserInteraction()
        // Trigger content change for file upload
        if (onContentChange) {
          onContentChange()
        }
      }
    }

    // Additional Trix attachment events
    const attachmentAddHandler = () => {
      onUserInteraction()
      if (onContentChange) {
        onContentChange()
      }
    }

    // Add all event listeners
    trixEditor.addEventListener("keydown", keydownHandler)
    trixEditor.addEventListener("mousedown", mousedownHandler)
    trixEditor.addEventListener("trix-attachment-remove", attachmentRemoveHandler)
    trixEditor.addEventListener("trix-attachment-add", attachmentAddHandler)
    window.addEventListener("file-uploaded", fileUploadedHandler)

    // Return cleanup function that removes all listeners
    return {
      cleanup: () => {
        trixEditor.removeEventListener("keydown", keydownHandler)
        trixEditor.removeEventListener("mousedown", mousedownHandler)
        trixEditor.removeEventListener("trix-attachment-remove", attachmentRemoveHandler)
        trixEditor.removeEventListener("trix-attachment-add", attachmentAddHandler)
        window.removeEventListener("file-uploaded", fileUploadedHandler)
      }
    }
  }
}
